import ally from 'ally.js/ally';
import * as helpers from '../helpers';

/**
 * Class represents Menu.
 */
class Menu {
    /**
     * Create base for menu.
     * @param {string} selector - selector for megamenu.
     */
    constructor(selector) {
        this.selector = selector;
        this.wrapper = null;
        this.list = null;
        this.items = null;
        this.itemsWithDropdown = null;
        this.links = null;
        this.toggles = null;
        this.submenus = null;
        this.dropdownWrappers = null;
        this.dropdownContainers = null;
        this.dropdownCloseButtons = null;
        this.closeButton = null;
        this.openerButton = null;
        this.lastClickedLink = null;
        this.lastEvent = null;
        this.menuPosition = null;
        this.comments = {};
        this.contextFocusableElements = null;
        this.$keyHandlers = null;

        this.options = {
            menu: {
                type: 'aside',
                className: 'js-mnv',
                listClassName: 'js-mnv-list',
                itemClassName: 'js-mnv-item',
                itemWithDropdownClassName: 'js-mnv-item-has-dropdown',
                linkClassName: 'js-mnv-link',
                wrapperClassName: 'js-mnv-wrapper',
                dropdownWrapperClassName: 'js-mnv-dropdown-wrapper',
                dropdownContainerClassName: 'js-mnv-dropdown-container',
                dropdownCloseClassName: 'js-mnv-dropdown-close',
                submenuClassName: 'js-mnv-submenu',
                toggleClassName: 'js-mnv-submenu-toggle',
                openedClassName: '-is-open',
                generatedClassName: '-is-generated',
                currentClassName: ['-is-current', 'is-active'],
            },
            overlay: {
                show: true,
                text: 'Close',
                className: 'js-mnv-overlay',
            },
            closeButton: {
                show: true,
                text: 'Close',
                className: 'js-mnv-close',
            },
            openerButton: {
                show: true,
                text: '',
                className: 'js-mnv-toggle',
            },
            body: {
                openedClassName: 'mnv-opened',
            },
            breakpoints: ['tablet', 'smartphone'],
            replaceTo: 'site-header',
            rebuildMenu: [],
            initSubmenuToggles: ['tablet', 'smartphone'],
            closeAllChildren: false,
            submenusHandler: {
                closeAllBeforeOpen: false,
                breakpoints: ['desktop'],
            },
            emitInsideHandlerFor: '',
            focusTrap: {
                init: true,
                closeOnLastElementOut: false,
            },
            messages: {
                close: 'Close',
            },
            beforeOpenMenu: () => {
            },
        };
    }

    /**
     * Get menu wrapper.
     * @returns {node} - Wrapper dom node.
     */
    getMenuWrapper() {
        return this.selector.children[0];
    }

    /**
     * Get menu dropdown wrappers.
     * @returns {array} - Nodes array of dropdown wrappers.
     */
    getMenuDropdownWrappers() {
        if (this.items) {
            return this.items
                .filter(item => item.querySelector('div'))
                .map(item => item.querySelector('div'));
        }

        this.items = this.getMenuItems();
        return this.items ? this.getMenuDropdownWrappers() : null;
    }

    /**
     * Get menu dropdown containers.
     * @returns {array} - Nodes array of dropdown containers.
     */
    getMenuDropdownContainers() {
        if (this.dropdownWrappers) {
            return this.dropdownWrappers.map(wrapper => wrapper.querySelector('div'));
        }

        this.dropdownWrappers = this.getMenuDropdownWrappers();
        return this.dropdownWrappers ? this.getMenuDropdownContainers() : null;
    }

    /**
     * Get all dropdown close buttons inside menu list.
     * @returns {array} - Nodes array of dropdown close buttons.
     */
    getMenuDropdownCloseButtons() {
        if (this.dropdownContainers) {
            return this.dropdownContainers.map(container => container.querySelector('button'));
        }

        this.dropdownContainers = this.getMenuDropdownContainers();
        return this.dropdownContainers ? this.getMenuDropdownCloseButtons() : null;
    }

    /**
     * Get main menu list node.
     * @returns {Node} - Menu list node.
     */
    getMenuList() {
        if (this.wrapper) {
            return this.wrapper.querySelector('ul.mnv-menu__list');
        }

        return this.getMenuWrapper().querySelector('ul.mnv-menu__list');
    }

    /**
     * Get all menu items.
     * @returns {array} - Items array.
     */
    getMenuItems() {
        return [...this.list.querySelectorAll('li.mnv-menu__item')];
    }

    /**
     * Get all menu items which has submenus.
     * @returns {array} - Items with dropdowns array.
     */
    getMenuItemsWithDropdown() {
        if (this.items) {
            return this.items.filter(item => item.querySelector('ul'));
        }
        this.items = this.getMenuItems();
        return this.items ? this.getMenuItemsWithDropdown() : null;
    }

    /**
     * Get all menu links
     * @returns {array} - Links array.
     */
    getMenuLinks() {
        return [...this.list.querySelectorAll('a.mnv-menu__link')];
    }

    /**
     * Get all menu toggels (buttons which manipulates submenus)
     * @returns {array} - Toggels array.
     */
    getMenuToggles() {
        return [...this.list.querySelectorAll(`button:not(.${this.options.closeButton.className})`)];
    }

    /**
     * Get all submenus.
     * @returns {array} - Submenus array.
     */
    getMenuSubmenus() {
        if (this.items) {
            return this.items
                .filter(item => item.querySelector('ul'))
                .map(item => item.querySelector('ul'));
        }

        this.items = this.getMenuItems();
        return this.items ? this.getMenuSubmenus() : null;
    }

    /**
     * Get close button for all menu.
     * @returns {node} - Close menu button node.
     */
    getCloseButton() {
        return this.selector.querySelector(`.${this.options.closeButton.className}`);
    }

    /**
     * Get opener button for all menu.
     * @returns {node} - Opener button node.
     */
    getOpenerButton() {
        return document.querySelector(`.${this.options.openerButton.className}`);
    }

    /**
     * Get menu overlay.
     * @returns {node|null} - Overlay node or null if we haven't it.
     */
    getOverlay() {
        const element = this.selector.nextElementSibling;

        if (element && helpers.hasClass(element, this.options.overlay.className)) {
            return element;
        }

        return null;
    }

    /**
     * Get all focusable elements on the top level menu.
     * @returns {array} - Nodes array of focusable elements.
     */
    getFirstLevelFocusableElements() {
        if (this.list) {
            const elements = helpers.getFocusableElements(this.list);

            if (elements) {
                let depth = 10000;
                return elements.filter((element) => {
                    const index = Menu.getDepthIndex(element, this.selector);

                    if (index <= depth) {
                        depth = index;
                        return element;
                    }

                    return false;
                });
            }
        }

        this.list = this.getMenuList();
        return this.list ? this.getFirstLevelFocusableElements() : null;
    }

    /**
     * Get first level links.
     * @returns {array} - Nodes array of first level links.
     */
    getFirstLevelLinks() {
        if (this.links) {
            let depth = 10000;
            return this.links.filter((link) => {
                const index = Menu.getDepthIndex(link, this.selector);

                if (index <= depth) {
                    depth = index;
                    return link;
                }

                return false;
            });
        }

        this.links = this.getMenuLinks();
        return this.links ? this.getFirstLevelLinks() : null;
    }

    /**
     * Get all opened items.
     * @returns {array} - Nodes array of opened items.
     */
    getOpenedItems() {
        return this.items.filter(item => helpers.hasClass(item, this.options.menu.openedClassName));
    }

    /**
     * Get all opened submenus.
     * @returns {array} - Nodes array of opened submenus.
     */
    getOpenedSubmenus() {
        return this.submenus.filter(submenu => helpers.hasClass(submenu.parentNode, this.options.menu.openedClassName));
    }

    /**
     * Get opened children inside element.
     * @param element {node} - element in which we should search.
     * @returns {array} - Array of opened items inside element.
     */
    getOpenedChildren(element) {
        const children = [...element.children];
        if (children) {
            return children.filter(item => helpers.hasClass(item, this.options.menu.openedClassName));
        }

        return null;
    }

    /**
     * Set class to elements.
     * @param elements {array} - Array of elements.
     * @param className {string} - Class which need to add.
     */
    setElementsClass(elements, className) {
        if (elements) {
            elements.forEach(element => helpers.addClass(element, className));
        }
    }

    /**
     * Create new overlay element.
     * @returns {HTMLElement} - New overlay element.
     */
    createOverlay() {
        const element = document.createElement('div');
        element.innerHTML = `<span class="ghost">${this.options.overlay.text}</span>`;
        helpers.addClass(element, this.options.overlay.className);
        return element;
    }

    /**
     * Create new close button element.
     * @param html {string} - String or markup to put inside close button.
     * @returns {HTMLElement} - New close button.
     */
    createCloseButton(html) {
        const button = document.createElement('button');
        helpers.addClass(button, this.options.closeButton.className);
        button.setAttribute('type', 'button');
        button.innerHTML = html || this.options.closeButton.text;
        return button;
    }

    /**
     * Add close button before/after element.
     * @param element {node} - node before/after we should paste.
     * @param position {string} - position to insert.
     */
    addCloseButton(element, position) {
        this.closeButton = this.createCloseButton();

        if (position === 'before') {
            element.parentNode.insertBefore(this.closeButton, element);
        } else {
            helpers.insertAfter(this.closeButton, element);
        }
    }

    /**
     * Add overlay after menu.
     */
    addOverlay() {
        if (this.overlay) {
            return helpers.insertAfter(this.overlay, this.selector);
        }

        this.overlay = this.createOverlay();
        return helpers.insertAfter(this.overlay, this.selector);
    }

    /**
     * Insert toggle button after node.
     * @param node {node} - node after we must insert.
     * @returns {HTMLElement} - inserted toggle.
     */
    addToggleAfter(node) {
        if (node) {
            const toggle = this.createToggle(node.textContent);
            helpers.insertAfter(toggle, node);
            return toggle;
        }
    }

    /**
     * Insert toggle button before node.
     * @param node {node} - node before we must insert.
     * @returns {HTMLElement} - inserted toggle.
     */
    addToggleBefore(node) {
        if (node) {
            const toggle = this.createToggle(node.textContent);
            node.parentNode.insertBefore(toggle, node);
            return toggle;
        }
    }

    /**
     * Create new toggle button.
     * @param text {string} - text inside toggle button.
     * @returns {HTMLElement} - Toggle element.
     */
    createToggle(text) {
        const toggle = document.createElement('button');
        toggle.setAttribute('type', 'button');
        toggle.innerHTML = `<span class="ghost">${text}</span>`;
        helpers.addClass(toggle, this.options.menu.toggleClassName);
        helpers.addClass(toggle, this.options.menu.generatedClassName);
        return toggle;
    }

    /**
     * Regenerate overlay.
     */
    setOverlay() {
        if (this.options.overlay) {
            this.onResponsive(this.options.overlay.show,
                () => {
                    this.addOverlay();
                },
                () => {
                    this.overlay = this.getOverlay();
                    this.removeElement(this.overlay);
                });
        }
    }

    /**
     * Generate close button if needs.
     */
    setCloseButton() {
        if (this.options.closeButton) {
            this.closeButton = this.getCloseButton();

            // if (!this.closeButton) {
            //     this.addCloseButton(this.list, 'before');
            // }
        }
    }

    /**
     * Remove element from dom.
     * @param element {node} - node to remove.
     */
    removeElement(element) {
        if (element) {
            element.parentNode.removeChild(element);
        }
    }

    /**
     * Set class to element.
     * @param element {node} - element node.
     * @param className {string} - Class to add.
     */
    setElementClass(element, className) {
        if (element) {
            helpers.addClass(element, className);
        }
    }

    /**
     * Remove class from elements.
     * @param elements {array} - elements array.
     * @param className {string} - Class to remove
     */
    removeElementsClass(elements, className) {
        if (elements) {
            elements.forEach(element => helpers.removeClass(element, className));
        }
    }

    /**
     * Remove class from element.
     * @param element {node} - element node.
     * @param className {string} - Class to remove
     */
    removeElementClass(element, className) {
        if (element) {
            helpers.removeClass(element, className);
        }
    }

    /**
     * Set helper class names for all megamenu elements.
     */
    setClassNames() {
        // Add helper class for menu element
        this.setElementClass(this.selector, this.options.menu.className);

        // Add helper class for menu inner wrapper
        this.wrapper = this.selector.children[0];
        this.setElementClass(this.wrapper, this.options.menu.wrapperClassName);

        // Add helper class for menu list
        this.list = this.getMenuList();
        this.setElementClass(this.list, this.options.menu.listClassName);

        // Add helper class for menu items
        this.items = this.getMenuItems();
        this.setElementsClass(this.items, this.options.menu.itemClassName);

        // Add helper class for menu items with dropdown
        this.itemsWithDropdown = this.getMenuItemsWithDropdown();
        this.setElementsClass(this.itemsWithDropdown, this.options.menu.itemWithDropdownClassName);

        // Add helper class for menu links
        this.links = this.getMenuLinks();
        this.setElementsClass(this.links, this.options.menu.linkClassName);

        // Add helper class for toggle buttons
        this.toggles = this.getMenuToggles();
        this.setElementsClass(this.toggles, this.options.menu.toggleClassName);

        // Add helper class for submenus
        this.submenus = this.getMenuSubmenus();
        this.setElementsClass(this.submenus, this.options.menu.submenuClassName);

        // Add helper class for dropdown wrappers
        this.dropdownWrappers = this.getMenuDropdownWrappers();
        this.setElementsClass(this.dropdownWrappers, this.options.menu.dropdownWrapperClassName);

        // Add helper class for dropdown containers
        this.dropdownContainers = this.getMenuDropdownContainers();
        this.setElementsClass(this.dropdownContainers, this.options.menu.dropdownContainerClassName);

        // Add helper class for dropdown close buttons
        this.dropdownCloseButtons = this.getMenuDropdownCloseButtons();
        this.setElementsClass(this.dropdownCloseButtons, this.options.menu.dropdownCloseClassName);
    }

    /**
     * Get current link.
     * @returns {array} - current link array (because we have different classes with drupal).
     */
    getCurrentLink() {
        return this.options.menu.currentClassName.map(className => this.selector.querySelector(`a.${className}`));
    }

    /**
     * Add attributes for current link and all parents.
     */
    initCurrent() {
        const current = this.getCurrentLink()[0];

        if (current) {
            let parent = current.parentNode;
            current.setAttribute('aria-current', 'page');

            while (!helpers.hasClass(parent, this.options.menu.wrapperClassName)) {
                if (helpers.hasClass(parent, this.options.menu.itemClassName)) {
                    const link = parent.querySelector(`.${this.options.menu.linkClassName}`);
                    const toggle = parent.querySelector(`.${this.options.menu.toggleClassName}`);

                    if (link !== current) {
                        link.setAttribute('aria-current', true);
                    }

                    parent.setAttribute('data-has-current', true);
                    // this.onResponsive(this.options.breakpoints, () => {
                    //     if (toggle) {
                    //         toggle.click();
                    //     }
                    //
                    //     this.setElementClass(parent, this.options.menu.openedClassName);
                    // }, () => {
                    //     if (helpers.hasClass(parent, this.options.menu.openedClassName) && toggle) {
                    //         toggle.click();
                    //     }
                    // });
                }

                parent = parent.parentNode;
            }
        }
    }

    /**
     * Open submenu and change a11y for it.
     * @param item {node} - Toggle button connected with submenu.
     */
    openSubmenu(item) {
        const parent = item.parentNode;
        const submenu = parent.querySelector('ul');
        const children = [...submenu.children];

        if (this.options.submenusHandler.closeAllBeforeOpen) {
            const siblings = helpers.getSiblings(parent);

            this.onResponsive(this.options.submenusHandler.breakpoints, () => {
                if (siblings.length) {
                    siblings
                        .filter(siblingItem => helpers.hasClass(siblingItem, this.options.menu.openedClassName))
                        .map(openedItem => openedItem.querySelector(`.${this.options.menu.toggleClassName}`))
                        .forEach(toggle => this.closeSubmenu(toggle));
                }
            });
        }

        this.setElementClass(parent, this.options.menu.openedClassName);
        helpers.setAriaControls(item, submenu, 'opened');

        children.forEach(child => helpers.setChildrenTabindex(child, 0));

        if (this.getOpenedItems().length) {
            helpers.addClass(this.list, this.options.menu.openedClassName);
        }
    }

    /**
     * Close submenu and change a11y for it.
     * @param item {node} - Toggle button connected with submenu.
     */
    closeSubmenu(item) {
        const parent = item.parentNode;
        const submenu = parent.querySelector('ul');
        const children = [...submenu.children];

        this.removeElementClass(parent, this.options.menu.openedClassName);
        helpers.setAriaControls(item, submenu, 'closed');

        children.forEach(child => helpers.setTabindex(child, -1));

        // Close all child submenus if you hide parent
        if (this.options.closeAllChildren) {
            children
                .filter(child => helpers.hasClass(child, this.options.menu.openedClassName))
                .forEach((child) => {
                    const childToggle = child.querySelector(`.${this.options.menu.toggleClassName}`);
                    return childToggle ? this.closeSubmenu(childToggle) : null;
                });
        }

        if (!this.getOpenedItems().length) {
            helpers.removeClass(this.list, this.options.menu.openedClassName);
        }
    }

    /**
     * Toggle between open/close submenu.
     * @param event - Browser event.
     */
    toggleSubmenu(event) {
        const item = helpers.hasClass(event.target, this.options.menu.toggleClassName)
            ? event.target
            : helpers.findAncestor(event.target, this.options.menu.toggleClassName);

        return helpers.hasClass(item.parentNode, this.options.menu.openedClassName)
            ? this.closeSubmenu(item) : this.openSubmenu(item);
    }

    /**
     * Function which checks resolution and execute success or backwards function.
     * @param param {array} - array of resolutions ['desktop', 'tablet', 'mobile']
     * @param success {function} - function wich executes if your resolution matches with param.
     * @param backwards {function} - function wich executes if your resolution not matches with param.
     */
    onResponsive(param, success = () => {
    }, backwards = () => {
    }) {
        let counter = 0;

        if (typeof success !== 'function') {
            throw new Error(`${success} should be a function`);
        }

        if (param) {
            if (typeof param === 'boolean') {
                return success();
            }

            if (Array.isArray(param)) {
                // eslint-disable-next-line no-return-assign
                param.forEach(resolution => (window.responsive === resolution ? counter += 1 : counter));
            } else {
                throw new Error(`${param} should be an Array or Boolean`);
            }
        }

        if (counter) {
            return success();
        }

        return backwards();
    }

    /**
     * Replace custom elements from their position and insert it inside menu. And backwards.
     */
    insertCustomElements() {
        helpers.checkExpectedParams({
            array: [this.options.rebuildMenu],
        });

        this.options.rebuildMenu.forEach((rule, index) => {
            // Check required params before start
            helpers.checkExpectedParams({
                array: [rule.breakpoints],
                string: [rule.selector],
                function: [rule.callback],
            });

            const elements = [].slice.call(document.querySelectorAll(rule.selector)); // Select elements

            // Create and store comments on first load
            if (!Object.prototype.hasOwnProperty.call(this.comments, index)) {
                this.comments[index] = [];

                elements.forEach((element) => {
                    const commentText = element.classList[0];
                    const comment = document.createComment(commentText);

                    helpers.insertAfter(comment, element);
                    this.comments[index].push(comment);
                });
            }

            // Loop through elements and check if we need to put them into nav or back.
            elements.forEach((element, i) => {
                if (rule.breakpoints.indexOf(window.responsive) !== -1) { // If we need to put element to nav ...
                    // Check if it's already there
                    if (!this.wrapper.contains(element)) {
                        rule.callback(element, this.wrapper, this); // Run callback
                    }
                } else { // ... or put it back
                    const comment = this.comments[index][i];

                    const toggle = element.querySelector('.js-dropdown__toggle');
                    if (toggle && toggle.hasAttribute('tabindex')) {
                        toggle.removeAttribute('tabindex');
                    }

                    // Check if it's already there
                    if (comment.previousSibling !== null && comment.previousSibling !== element) {
                        comment.parentNode.insertBefore(element, comment);
                    }
                }
            });
        });
    }

    checkIncludedElements(e) {
        if (this.options.emitInsideHandlerFor.length) {
            const elements = [...document.querySelectorAll(this.options.emitInsideHandlerFor)];
            return elements.filter(element => element.contains(e.target)).length > 0;
        }

        return false;
    }

    /**
     * Close menu if you click outside selector.
     * @param e - Browser event.
     */
    clickOutside(e) {
        const isClickInside = this.selector.contains(e.target) || this.openerButton.contains(e.target) || this.checkIncludedElements(e);

        if (!isClickInside) {
            this.closeMenu();
        }
    }

    /**
     * Open menu
     */
    openMenu() {
    }

    /**
     * Close menu
     */
    closeMenu() {
    }

    /**
     * Toggle between open/close menu.
     * @returns {*}
     */
    toggleMenu() {
        return helpers.hasClass(this.selector, this.options.menu.openedClassName) ? this.closeMenu() : this.openMenu();
    }

    nextKeysHandler(event, content, otherElements) {
        event.preventDefault();

        const nextElement = helpers.getNextFocusableElement(event.target, content, otherElements);

        if (nextElement) {
            ally.element.focus(nextElement);
        }
    }

    prevKeysHandler(event, content, otherElements) {
        event.preventDefault();
        const prevElement = helpers.getPrevFocusableElement(event.target, content, otherElements);

        if (prevElement) {
            ally.element.focus(prevElement);
        }
    }

    /**
     * Init focus trap for menu.
     * @param event - Browser event.
     */
    initFocusTrap(event) {
        // helpers.initFocusTrap(this.selector, event, true);

        const elementOut = this.options.focusTrap.closeOnLastElementOut
            ? helpers.getLastFocusableElement(this.selector)
            : null;
        const closeCondition = this.options.focusTrap.closeOnLastElementOut
            ? event.keyCode === 27 || event.target === elementOut
            : event.keyCode === 27;

        if (closeCondition) {
            this.closeMenu();
        }
    }

    /**
     * Merge to objects.
     * @param defaults {object} - default object.
     * @param options {object} - object ot merge.
     * @returns {object} - merged object.
     */
    extend(defaults, options) {
        if (options instanceof Object) {
            return helpers.extendDefaults(defaults, options);
        }
    }

    /**
     * Get depth of DOM element tree.
     * @param element {HTMLElement} - element node in which whe should calc depth.
     * @returns {number} - depth value.
     */
    static getDepth(element) {
        let queue = [];
        let depth = -1;

        queue.push(element);

        while (queue.length > 0) {
            const innerQueue = [];

            while (queue.length > 0) {
                const tempNode = queue.shift();

                if (tempNode.children.length) {
                    [...tempNode.children].forEach((child) => {
                        innerQueue.push(child);
                    });
                }
            }

            depth += 1;
            queue = innerQueue;
        }

        return depth;
    }

    /**
     * Get depth position of element inside wrapper.
     * @param element {HTMLElement} - element node to take depth index.
     * @param wrapper {HTMLElement} - element in which search.
     * @returns {number} - depth level.
     */
    static getDepthIndex(element, wrapper) {
        let elem = element;
        let index = 0;

        while (elem !== wrapper) {
            if (elem === document.body) {
                index = null;
                break;
            }

            index += 1;
            elem = elem.parentNode;
        }

        return index;
    }
}

/**
 * Class represents Aside menu.
 */
class AsideMenu extends Menu {
    /**
     * Create menu instance
     * @param selector {string} - element selector.
     * @param options {object} - object with menu settings.
     */
    constructor(selector, options) {
        super(selector);

        this.options = options;
        this.create();
    }

    /**
     * Init A11y for menu.
     */
    initAccessibility() {
        // Create a11y connections between controls and menu
        const togglesArray = [];
        if (this.overlay) {
            togglesArray.push(this.overlay);
        }

        if (this.closeButton) {
            togglesArray.push(this.closeButton);
        }

        togglesArray.push(this.openerButton);

        helpers.initAriaControls(togglesArray, this.selector);

        // Create a11y connections between controls and submenu
        if (this.toggles) {
            this.toggles.forEach((toggle) => {
                const submenu = toggle.parentNode.querySelector(`.${this.options.menu.submenuClassName}`);
                helpers.initAriaControls(toggle, submenu);
            });
        }

        // Set tabindex to -1 for all menu
        helpers.setTabindex(this.selector, -1);

        const contextBlock = document.querySelector('.header-bar__components');
        if (contextBlock) {
            this.contextFocusableElements = ally.query.focusable({ context: contextBlock });
        }
    }

    /**
     * Create menu structure, set classes, events.
     */
    create() {
        this.setClassNames();

        if (this.options.openerButton || this.options.overlay || this.options.closeButton) {
            this.openerButton = this.getOpenerButton();

            if (!this.openerButton) {
                throw new Error(`Please add opener button with class: ${this.options.openerButton.className}`);
            }

            this.setOverlay();
            this.setCloseButton();
        } else {
            throw new Error(`Please add opener button with class: ${this.options.openerButton.className}`);
        }

        this.initAccessibility();
        this.initEvents();
        this.initCurrent();
        this.insertCustomElements();
    }

    /**
     * Initialize menu events.
     */
    initEvents() {
        this.toggleMenuHandler = this.toggleMenu.bind(this);
        this.closeMenuHandler = this.closeMenu.bind(this);
        this.updateMenuHandler = this.update.bind(this);
        this.closeMenuOnEscHandler = this.closeMenuOnEsc.bind(this);
        // this.outsideHandler = this.clickOutside.bind(this);

        this.openerButton.addEventListener('click', this.toggleMenuHandler);

        [this.closeButton, this.overlay].forEach((element) => {
            if (element) {
                element.addEventListener('click', this.closeMenuHandler);
            }
        });

        if (this.toggles) {
            this.toggleSubmenuHandler = this.toggleSubmenu.bind(this);
            this.toggles.forEach(toggle => toggle.addEventListener('click', this.toggleSubmenuHandler));
        }

        if (this.options.focusTrap.init) {
            this.focusTrapHandler = this.initFocusTrap.bind(this);
            this.selector.addEventListener('keydown', this.focusTrapHandler);
        }

        window.addEventListener('responsive', this.updateMenuHandler);
        document.addEventListener('click', this.outsideHandler);
        document.addEventListener('keydown', this.closeMenuOnEscHandler);
    }

    /**
     * Remove menu events.
     */
    removeEvents() {
        this.openerButton.removeEventListener('click', this.toggleMenuHandler);

        [this.closeButton, this.overlay].forEach((element) => {
            if (element) {
                element.removeEventListener('click', this.closeMenuHandler);
            }
        });

        if (this.toggles) {
            this.toggles.forEach(toggle => toggle.removeEventListener('click', this.toggleSubmenuHandler));
        }

        if (this.options.focusTrap.init) {
            this.selector.removeEventListener('keydown', this.focusTrapHandler);
        }

        window.removeEventListener('responsive', this.updateMenuHandler);
        document.removeEventListener('click', this.outsideHandler);
    }

    /**
     * Open menu.
     */
    openMenu() {
        this.options.beforeOpenMenu(this);
        this.setElementClass(this.selector, this.options.menu.openedClassName);
        this.setElementClass(this.overlay, this.options.menu.openedClassName);
        this.setElementClass(document.body, this.options.body.openedClassName);

        if (this.closeButton) {
            helpers.toggleTabindex(this.closeButton);
        }

        helpers.manageBodyOverflow('hidden');

        if (this.overlay && this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.overlay, this.closeButton], this.selector, 'opened');
        } else if (this.overlay) {
            helpers.setAriaControls([this.openerButton, this.overlay], this.selector, 'opened');
        } else if (this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.closeButton], this.selector, 'opened');
        } else {
            helpers.setAriaControls(this.openerButton, this.selector, 'opened');
        }

        this.setElementClass(this.openerButton, this.options.menu.openedClassName);

        const firstLevelElements = this.getFirstLevelFocusableElements();

        if (firstLevelElements) {
            const firstLevelIndex = AsideMenu.getDepthIndex(firstLevelElements[0], this.selector);
            helpers.manageTabindex(this.selector, 0, firstLevelIndex);

            const openedItems = this.getOpenedItems();

            if (openedItems) {
                openedItems.forEach((item) => {
                    helpers.manageTabindex(item, 0, 3);
                });
            }
        }

        this.contextFocusableElements.forEach((el) => {
            el.setAttribute('tabindex', 0);
        });

        this.$keyHandlers = ally.when.key({
            // context: popup,
            tab: (e) => {
                this.nextKeysHandler(e, this.selector, this.contextFocusableElements);
            },
            'shift+tab': (e) => {
                this.prevKeysHandler(e, this.selector, this.contextFocusableElements);
            },
        });

        // When open set focus on close button
        this.openerButton.focus();

        this.replaceButtonText(this.openerButton);
    }

    /**
     * Close menu.
     */
    closeMenu() {
        if (helpers.hasClass(this.selector, this.options.menu.openedClassName)) {
            this.onResponsive(this.options.openerButton.show, () => {
                this.openerButton.focus();
            });

            this.replaceButtonText(this.openerButton);
        }

        this.removeElementClass(this.selector, this.options.menu.openedClassName);
        this.removeElementClass(this.overlay, this.options.menu.openedClassName);
        this.removeElementClass(document.body, this.options.body.openedClassName);

        helpers.manageBodyOverflow('visible');
        helpers.setTabindex(this.selector, -1);

        if (this.overlay && this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.overlay, this.closeButton], this.selector, 'closed');
        } else if (this.overlay) {
            helpers.setAriaControls([this.openerButton, this.overlay], this.selector, 'closed');
        } else if (this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.closeButton], this.selector, 'closed');
        } else {
            helpers.setAriaControls(this.openerButton, this.selector, 'closed');
        }

        this.contextFocusableElements.forEach((el) => {
            el.removeAttribute('tabindex');
        });

        if (this.$keyHandlers) {
            this.$keyHandlers.disengage();
        }

        this.removeElementClass(this.openerButton, this.options.menu.openedClassName);
    }

    closeMenuOnEsc(event) {
        if (event) {
            if (event.keyCode === 27) {
                this.closeMenu();
            }
        }
    }

    /**
     * Toggle text in button
     */
    replaceButtonText(button) {
        const altText = button.getAttribute('data-alt-text');
        const defaultText = button.lastElementChild.innerText;

        if (altText) {
            button.lastElementChild.innerText = altText;
            button.setAttribute('data-alt-text', defaultText);
        }
    }

    /**
     * Update menu structure and handlers.
     */
    update() {
        this.closeMenu();
        this.closeMenuOnEsc();
        this.removeEvents();
        this.setOverlay();
        this.setCloseButton();
        this.initAccessibility();
        this.insertCustomElements();
        this.initEvents();
    }
}

/**
 * Class represents Default menu.
 */
class DefaultMenu extends Menu {
    /**
     * Create menu instance
     * @param selector {string} - element selector.
     * @param options {object} - object with menu settings.
     */
    constructor(selector, options) {
        super(selector);

        this.options = options;
        this.create();
    }

    /**
     * Create menu structure, set classes, events.
     */
    create() {
        this.setClassNames();

        // Create comment node to remember menu position
        const comment = document.createComment(this.selector.className);
        this.menuPosition = this.selector.parentNode.insertBefore(comment, this.selector);

        if (this.options.openerButton || this.options.overlay || this.options.closeButton) {
            this.openerButton = this.getOpenerButton();

            if (!this.openerButton) {
                throw new Error(`Please add opener button with class: ${this.options.openerButton.className}`);
            }

            this.setOverlay();
            this.setCloseButton();
        } else {
            throw new Error(`Please add opener button with class: ${this.options.openerButton.className}`);
        }

        this.insertCustomElements();
        this.initAccessibility();
        this.generateToggels();
        this.initEvents();
        this.initCurrent();
        this.updateMenuPosition();
    }

    /**
     * Init A11y for menu.
     */
    initAccessibility() {
        // Create a11y connections between controls and menu
        const togglesArray = [];
        if (this.overlay) {
            togglesArray.push(this.overlay);
        }

        if (this.closeButton) {
            togglesArray.push(this.closeButton);
        }

        togglesArray.push(this.openerButton);

        helpers.initAriaControls(togglesArray, this.selector);

        // Create a11y connections between controls and submenu
        this.onResponsive(this.options.initSubmenuToggles,
            () => {
                if (this.toggles) {
                    this.toggles
                        .filter(toggle => !helpers.hasClass(toggle, this.options.menu.dropdownCloseClassName))
                        .forEach((toggle) => {
                            const submenu = toggle.parentNode.querySelector(`.${this.options.menu.submenuClassName}`);
                            helpers.initAriaControls(toggle, submenu);
                        });
                }
            },
            () => {
                if (this.toggles) {
                    this.toggles
                        .filter(toggle => !helpers.hasClass(toggle, this.options.menu.dropdownCloseClassName))
                        .forEach((toggle) => {
                            const submenu = toggle.parentNode.querySelector(`.${this.options.menu.submenuClassName}`);
                            helpers.removeAriaControls(toggle);
                            helpers.removeAriaControls(submenu);
                        });
                }
            });

        // Create a11y connections between first level links and dropdowns
        const links = this.getFirstLevelLinks();
        links.forEach((link) => {
            // link.setAttribute('tabindex', 0);
            const dropdownWrapper = link.parentNode.querySelector(`.${this.options.menu.dropdownWrapperClassName}`);
            const closeButton = dropdownWrapper
                ? dropdownWrapper.querySelector(`.${this.options.menu.dropdownCloseClassName}`)
                : null;

            if (dropdownWrapper && closeButton) {
                helpers.initAriaControls([link, closeButton], dropdownWrapper);
            } else if (dropdownWrapper) {
                helpers.initAriaControls(link, dropdownWrapper);
            }

            this.onResponsive(this.options.breakpoints, () => {
            }, () => {
                link.removeAttribute('tabindex');
            });

            if (helpers.hasClass(link.parentNode, this.options.menu.itemWithDropdownClassName)) {
                this.onResponsive(this.options.breakpoints, () => {
                    link.removeAttribute('role');
                }, () => {
                    link.setAttribute('role', 'button');
                });
            }
        });

        this.onResponsive(this.options.breakpoints, () => {
            this.selector.setAttribute('aria-hidden', true);
            helpers.setTabindex(this.selector, -1);
        }, () => {
            this.selector.setAttribute('aria-hidden', false);
            // Set tabindex to -1 for all menu containers
            if (this.dropdownContainers) {
                this.dropdownContainers.forEach(container => helpers.setTabindex(container, -1));
            }
        });
    }

    /**
     * Initialize menu events.
     */
    initEvents() {
        this.toggleMenuHandler = this.toggleMenu.bind(this);
        this.outsideHandler = this.clickOutside.bind(this);
        this.toggleMobileMenuHandler = this.toggleMobileMenu.bind(this);
        this.updateMenuHandler = this.update.bind(this);
        this.toggleSubmenuHandler = this.toggleSubmenu.bind(this);
        this.scrollHandler = this.closeMenuOnScroll.bind(this);

        [this.openerButton, this.closeButton, this.overlay].forEach((element) => {
            if (element) {
                element.addEventListener('click', this.toggleMobileMenuHandler);
            }
        });

        const links = this.getFirstLevelLinks();
        if (links) {
            links
                .filter(link => link.parentNode.querySelector(`.${this.options.menu.dropdownWrapperClassName}`))
                .forEach((link) => {
                    ['click', 'keydown'].forEach((evenType) => {
                        link.addEventListener(evenType, this.toggleMenuHandler);
                    });
                });

            const toggles = links
                .filter(link => link.parentNode.querySelector(`.${this.options.menu.toggleClassName}`))
                .map(link => link.parentNode.querySelector(`.${this.options.menu.toggleClassName}`))
                .forEach(link => link.addEventListener('click', this.toggleMenuHandler));
        }

        if (this.toggles) {
            const submenuToggles = this.toggles
                .filter(toggle => helpers.hasClass(toggle.nextElementSibling, this.options.menu.submenuClassName));

            this.onResponsive(this.options.initSubmenuToggles, () => {
                submenuToggles.forEach(toggle => toggle.addEventListener('click', this.toggleSubmenuHandler));
            }, () => {
                submenuToggles.forEach(toggle => toggle.removeEventListener('click', this.toggleSubmenuHandler));
            });
        }

        if (this.options.focusTrap.init) {
            this.focusTrapHandler = this.initFocusTrap.bind(this);
            this.onResponsive(this.options.breakpoints, () => {
                this.selector.addEventListener('keydown', this.focusTrapHandler);
            }, () => {
                this.selector.removeEventListener('keydown', this.focusTrapHandler);
            });
        }

        document.addEventListener('click', this.outsideHandler);
        window.addEventListener('responsive', this.updateMenuHandler);
        window.addEventListener('scroll', this.scrollHandler);
    }

    /**
     * Remove menu events.
     */
    removeEvents() {
        [this.openerButton, this.closeButton, this.overlay].forEach((element) => {
            if (element) {
                element.removeEventListener('click', this.toggleMobileMenuHandler);
            }
        });

        const links = this.getFirstLevelLinks();
        if (links) {
            links
                .filter(link => link.parentNode.querySelector(`.${this.options.menu.dropdownWrapperClassName}`))
                .forEach((link) => {
                    ['click', 'keydown'].forEach((evenType) => {
                        link.removeEventListener(evenType, this.toggleMenuHandler);
                    });
                });

            const toggles = links
                .filter(link => link.parentNode.querySelector(`.${this.options.menu.toggleClassName}`))
                .map(link => link.parentNode.querySelector(`.${this.options.menu.toggleClassName}`))
                .forEach(link => link.removeEventListener('click', this.toggleMenuHandler));
        }

        if (this.toggles) {
            const submenuToggles = this.toggles
                .filter(toggle => helpers.hasClass(toggle.nextElementSibling, this.options.menu.submenuClassName))
                .forEach(toggle => toggle.removeEventListener('click', this.toggleSubmenuHandler));
        }

        if (this.options.focusTrap.init) {
            this.selector.removeEventListener('keydown', this.focusTrapHandler);
        }

        document.removeEventListener('click', this.outsideHandler);
        window.removeEventListener('responsive', this.updateMenuHandler);
    }

    closeMenuOnScroll(event) {
        if (this.lastClickedLink) {
            if (this.getOpenedItems().length) {
                this.closeMenu(this.lastClickedLink.parentNode, this.lastEvent);
            }
        }
    }

    /**
     * Open mobile menu.
     */
    openMobileMenu() {
        this.setElementClass(this.selector, this.options.menu.openedClassName);
        this.setElementClass(this.overlay, this.options.menu.openedClassName);
        this.setElementClass(document.body, this.options.body.openedClassName);

        this.selector.setAttribute('aria-hidden', false);

        if (this.closeButton) {
            this.closeButton.setAttribute('tabindex', 0);
        }

        helpers.manageBodyOverflow('hidden');

        if (this.overlay && this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.overlay, this.closeButton], this.selector, 'opened');
        } else if (this.overlay) {
            helpers.setAriaControls([this.openerButton, this.overlay], this.selector, 'opened');
        } else if (this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.closeButton], this.selector, 'opened');
        } else {
            helpers.setAriaControls(this.openerButton, this.selector, 'opened');
        }

        this.setElementClass(this.openerButton, this.options.menu.openedClassName);

        const firstLevelElements = this.getFirstLevelFocusableElements();

        if (firstLevelElements) {
            const firstLevelIndex = AsideMenu.getDepthIndex(firstLevelElements[0], this.selector);
            helpers.manageTabindex(this.selector, 0, firstLevelIndex);

            const openedItems = this.getOpenedItems();

            if (openedItems) {
                openedItems.forEach((item) => {
                    helpers.manageTabindex(item, 0, 3);
                });
            }
        }

        const focusableElements = helpers.getFocusableElements(this.selector, true);

        // When open set focus on first element
        if (focusableElements) {
            helpers.setFirstElementFocus(focusableElements);
        }
    }

    /**
     * Close mobile menu.
     */
    closeMobileMenu() {
        if (helpers.hasClass(this.selector, this.options.menu.openedClassName)) {
            this.onResponsive(this.options.openerButton.show, () => {
                this.openerButton.focus();
            });
        }

        this.removeElementClass(this.selector, this.options.menu.openedClassName);
        this.removeElementClass(this.overlay, this.options.menu.openedClassName);

        this.selector.setAttribute('aria-hidden', true);

        helpers.manageBodyOverflow('visible');
        helpers.setTabindex(this.selector, -1);

        if (this.overlay && this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.overlay, this.closeButton], this.selector, 'closed');
        } else if (this.overlay) {
            helpers.setAriaControls([this.openerButton, this.overlay], this.selector, 'closed');
        } else if (this.closeButton) {
            helpers.setAriaControls([this.openerButton, this.closeButton], this.selector, 'closed');
        } else {
            helpers.setAriaControls(this.openerButton, this.selector, 'closed');
        }

        this.removeElementClass(this.openerButton, this.options.menu.openedClassName);

        this.onResponsive(this.options.breakpoints, () => {
            if (helpers.hasClass(document.body, this.options.body.openedClassName)) {
                this.openerButton.focus();
            }
        });

        this.removeElementClass(document.body, this.options.body.openedClassName);
    }

    /**
     * Toggle between open/close mobile menu.
     */
    toggleMobileMenu() {
        return helpers.hasClass(this.selector, this.options.menu.openedClassName) ? this.closeMobileMenu() : this.openMobileMenu();
    }

    /**
     * Open menu (first level)
     * @param item {HTMLElement} - Node which should be opened.
     * @param event - Browser event.
     */
    openMenu(item, event) {
        let eventIsDisabled = false;

        this.onResponsive(this.options.breakpoints, () => {
            // event.preventDefault();
            eventIsDisabled = false;
        }, () => {
            event.preventDefault();
            eventIsDisabled = true;
        });

        // Disable link on mobile view
        if (event.target && event.target.tagName.toLowerCase() === 'a' && !eventIsDisabled) {
            return false;
        }

        if (this.lastClickedLink) {
            this.onResponsive(this.options.breakpoints, () => {
            }, () => {
                this.closeMenu(this.lastClickedLink.parentNode, event);
            });
        }

        this.lastClickedLink = item.querySelector('a');
        this.lastEvent = event;

        this.setElementClass(item, this.options.menu.openedClassName);

        this.onResponsive(this.options.breakpoints, () => {
        }, () => {
            this.setElementClass(document.body, this.options.body.openedClassName);
        });

        const dropdown = item.querySelector(`.${this.options.menu.dropdownWrapperClassName}`);
        const toggle = this.lastClickedLink.nextElementSibling.tagName.toLowerCase() === 'button'
            ? this.lastClickedLink.nextElementSibling
            : null;
        const closeButton = dropdown
            ? dropdown.querySelector(`.${this.options.menu.dropdownCloseClassName}`)
            : null;

        if (dropdown && closeButton && toggle) {
            helpers.setAriaControls([this.lastClickedLink, closeButton, toggle], dropdown, 'opened');
        } else if (dropdown && closeButton) {
            helpers.setAriaControls([this.lastClickedLink, closeButton], dropdown, 'opened');
        } else if (dropdown) {
            helpers.setAriaControls(this.lastClickedLink, dropdown, 'opened');
        }

        const submenus = [...dropdown.querySelectorAll(`.${this.options.menu.submenuClassName}`)];

        // // When open set focus on first element (need timeout because we have visibility animation)
        this.onResponsive(this.options.breakpoints, () => {
            if (submenus.length) {
                submenus
                    .filter(submenu => helpers.hasClass(submenu.parentNode, this.options.menu.dropdownContainerClassName))
                    .forEach(submenu => helpers.manageTabindex(submenu, 0, 3));
            }
        }, () => {
            if (submenus.length) {
                submenus.forEach(submenu => (submenu.getAttribute('aria-hidden')
                    ? helpers.manageTabindex(dropdown, 0, 3)
                    : helpers.setTabindex(dropdown, 0)));
            }

            setTimeout(() => {
                helpers.setFirstElementFocus(helpers.getFocusableElements(dropdown, true));
            }, 100);
        });

        const openedItems = this.getOpenedItems();
        if (openedItems) {
            openedItems
                .filter((openedItem) => {
                    const wrapper = helpers.findAncestor(openedItem, this.options.menu.dropdownWrapperClassName);
                    return wrapper && wrapper.getAttribute('aria-hidden') === 'false';
                })
                .forEach(openedItem => helpers.manageTabindex(openedItem, 0, 3));

            helpers.addClass(this.list, this.options.menu.openedClassName);
        }

        this.onResponsive(this.options.breakpoints, () => {
        }, () => {
            this.getFirstLevelLinks().forEach(link => link.setAttribute('tabindex', -1));
            if (this.options.focusTrap.init) {
                dropdown.addEventListener('keydown', this.focusTrapHandler);
            }
        });
    }

    /**
     * Close menu (first level)
     * @param item {HTMLElement} - Node which should be closed.
     * @param event - Browser event.
     */
    closeMenu(item, event) {
        let eventIsDisabled = false;
        if (this.selector.contains(event.target)) {
            this.onResponsive(this.options.breakpoints, () => {
                // event.stopPropagation();
                eventIsDisabled = false;
            }, () => {
                event.preventDefault();
                eventIsDisabled = true;
            });
        }

        // Disable link on mobile view
        if (event.target && event.target.tagName.toLowerCase() === 'a' && !eventIsDisabled) {
            return false;
        }

        this.removeElementClass(item, this.options.menu.openedClassName);

        this.onResponsive(this.options.breakpoints, () => {
        }, () => {
            this.removeElementClass(document.body, this.options.body.openedClassName);
        });

        const dropdown = item.querySelector(`.${this.options.menu.dropdownWrapperClassName}`);
        const toggle = this.lastClickedLink && this.lastClickedLink.nextElementSibling
        && this.lastClickedLink.nextElementSibling.tagName.toLowerCase() === 'button'
            ? this.lastClickedLink.nextElementSibling
            : null;
        const closeButton = dropdown
            ? dropdown.querySelector(`.${this.options.menu.dropdownCloseClassName}`)
            : null;

        helpers.setTabindex(dropdown, -1);

        // Close all opened items inside current item
        const openedItems = [...item.querySelectorAll(`.${this.options.menu.itemClassName}.${this.options.menu.openedClassName}`)];
        if (openedItems) {
            openedItems.forEach((openedItem) => {
                const openedItemToggle = openedItem.querySelector(`.${this.options.menu.toggleClassName}`);
                if (openedItemToggle) {
                    openedItemToggle.click();
                }
            });
        }

        if (!this.getOpenedItems().length) {
            helpers.removeClass(this.list, this.options.menu.openedClassName);
        }

        if (dropdown && closeButton && toggle) {
            helpers.setAriaControls([this.lastClickedLink, closeButton, toggle], dropdown, 'closed');
        } else if (dropdown && closeButton) {
            helpers.setAriaControls([this.lastClickedLink, closeButton], dropdown, 'closed');
        } else if (dropdown) {
            helpers.setAriaControls(this.lastClickedLink, dropdown, 'closed');
        }

        this.onResponsive(this.options.breakpoints, () => {
        }, () => {
            this.getFirstLevelLinks().forEach(link => link.setAttribute('tabindex', 0));
            if (this.selector.contains(event.target)) {
                this.lastClickedLink.focus();
            }
            if (this.options.focusTrap.init) {
                dropdown.removeEventListener('keydown', this.focusTrapHandler);
            }
        });
    }

    /**
     * Toggle between open/close {first levetl} menu.
     * @param event - Browser event.
     */
    toggleMenu(event) {
        if (event.target && event.target.getAttribute('role') === 'button') {
            if (event.type === 'click' || event.keyCode === 13 || event.keyCode === 32) {
                const item = helpers.findAncestor(event.target, this.options.menu.itemClassName);
                return helpers.hasClass(item, this.options.menu.openedClassName)
                    ? this.closeMenu(item, event)
                    : this.openMenu(item, event);
            }
        } else {
            const item = helpers.findAncestor(event.target, this.options.menu.itemClassName);
            return helpers.hasClass(item, this.options.menu.openedClassName)
                ? this.closeMenu(item, event)
                : this.openMenu(item, event);
        }
    }

    /**
     * Close menu if you click outside selector.
     * @param event - Browser event.
     */
    clickOutside(e) {
        const isClickInside = this.selector.contains(e.target) || this.openerButton.contains(e.target) || this.checkIncludedElements(e);

        if (!isClickInside && this.lastClickedLink) {
            this.closeMenu(this.lastClickedLink.parentNode, e);
        }
    }

    /**
     * Init focus trap for menu.
     * @param event - Browser event.
     */
    initFocusTrap(event) {
        helpers.initFocusTrap(this.selector, event, true);

        const elementOut = this.options.focusTrap.closeOnLastElementOut
            ? helpers.getLastFocusableElement(this.selector)
            : null;
        const closeCondition = this.options.focusTrap.closeOnLastElementOut
            ? event.keyCode === 27 || event.target === elementOut
            : event.keyCode === 27;

        if (closeCondition) {
            this.closeMenu(this.lastClickedLink.parentNode, event);
        }
    }

    /**
     * Update menu position in DOM tree. (Mobile position can be changed)
     */
    updateMenuPosition() {
        // Replace menu
        this.onResponsive(this.options.breakpoints, () => {
            const container = document.querySelector(`.${this.options.replaceTo}`);

            if (container) {
                container.appendChild(this.selector);
            }
        }, () => {
            helpers.insertAfter(this.selector, this.menuPosition);
        });
    }

    /**
     * Generate Toggle buttons.
     */
    generateToggels() {
        this.onResponsive(this.options.breakpoints, () => {
            this.links.forEach((link) => {
                if (helpers.hasClass(link.parentNode, this.options.menu.itemWithDropdownClassName)) {
                    // If link already has connections
                    if (link.nextElementSibling.tagName.toLocaleLowerCase() !== 'button' && link.getAttribute('aria-controls')) {
                        const id = link.getAttribute('aria-controls');
                        const connectedElements = [...this.selector.querySelectorAll(`[aria-controls='${id}']`)];
                        const controlElement = this.selector.querySelector(`#${id}`);

                        // add button after link
                        const toggle = this.addToggleAfter(link);

                        // Reinit a11y for this elements
                        helpers.initAriaControls([...connectedElements, toggle], controlElement);
                    }
                }
            });
        }, () => {
            this.getMenuToggles().filter((toggle) => {
                return helpers.hasClass(toggle, this.options.menu.generatedClassName)
                    ? toggle.parentNode.removeChild(toggle)
                    : false;
            });
        });
    }

    /**
     * Update menu
     */
    update() {
        // Close last opened menu
        // if (this.lastClickedLink) {
        //     this.closeMenu(this.lastClickedLink.parentNode, this.lastEvent);
        // }

        // Close all opened menus
        if (this.getOpenedItems().length) {
            this.getOpenedItems().forEach((item) => {
                if (DefaultMenu.getDepthIndex(item, this.selector) <= 3) {
                    this.closeMenu(item, this.lastEvent);
                }
            });
        }

        // Close mobile menu
        this.closeMobileMenu();
        // Remove all event listeners
        this.removeEvents();
        // Update menu position if it needs
        this.updateMenuPosition();
        // Generate toggels if it needs
        this.generateToggels();
        // Regenerate overlay
        this.setOverlay();
        // Regenerate close button
        this.setCloseButton();
        // Reinit A11y
        this.initAccessibility();
        // Update current item
        this.initCurrent();
        // Replace custom elements if it needs
        this.insertCustomElements();
        // Init new event listeners
        this.initEvents();
    }
}

/**
 * Represents megamenu.
 */
export default class Megamenu extends Menu {
    /**
     * Create megamenu instance.
     * @param selector {string} - selector for megamenu.
     * @param options {object} - object with menu settings.
     */
    constructor(selector, options) {
        super(selector);

        this.options = this.extend(this.options, options);
        this.checkBeforeInit();
    }

    /**
     * Check before initializing menu.
     */
    checkBeforeInit() {
        if (typeof this.selector === 'string') {
            [...document.querySelectorAll(this.selector)].forEach(menu => new Megamenu(menu, this.options));
        } else {
            this.init();
        }
    }

    /**
     * Initialize megamenu.
     * @returns {Menu} - Menu object which will be created.
     */
    init() {
        switch (this.options.menu.type) {
            case 'aside':
                return new AsideMenu(this.selector, this.options);
            default:
                return new DefaultMenu(this.selector, this.options);
        }
    }
}
